import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { RHFTextField, RHFAutocomplete } from "@/src/components/hook-form";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { GENDER, SALUTE } from "@/src/lib/module-helper";
import { useDispatch, useSelector } from "react-redux";
import AddPatientFormHandler from "@/src/lib/module-helper/my-account/addPatient/form-handler";
import {
    useCreatePatient,
    useSignUp,
    useUpdatePatientDetails,
} from "@/src/lib/module-helper/my-account/hook/usePatientDetails";
import Iconify from "@/src/components/iconify";
import useCommon from "@/src/lib/util/useCommon";
import { userDetails } from "@/src/lib/store/user-details/slice";
import { updateHookCity } from "@/src/lib/store/hook-city/slice";
import { useToast } from "@/src/lib/hooks/useToast";
import FormProvider from "@/src/components/hook-form/FormProvider";
import { dateUtils, validateEmail } from "@/src/components/common/common";
import { myAccountApiService } from "@/src/lib/api-services/myAccountApiServices";
import IconCode from "@/src/lib/iconCode";
import { useCustomQuery } from "@/src/lib/hooks/useFetch";
import CustomButton from "@/src/lib/ui/Button";
import HodDesktopDatePicker from "@/src/lib/ui/hodDesktopDatePicker";
import { useHODMutation } from "@/src/lib/hooks/useHODMutation";
import { headerApiService } from "@/src/lib/api-services/headerApiServices";

const VALID_CHARACTERS_REGEX = /^[a-zA-Z0-9 ,.'-]+$/;

const CreateMemberForm = ({
    params = null,
    patientDetails,
    isFirstUser = {},
    handleClose,
    setIsStep,
    noRedirect = false,
    refetchPatientList,
}) => {
    const dispatch = useDispatch();
    const { cityLists } = useCommon();
    const { showCustomToast } = useToast();
    const { userInfo } = useSelector((state) => state?.user) || {};

    const [isValidatingEmail, setIsValidatingEmail] = useState(false);

    const { data, isLoading } = useCustomQuery(
        "email_Id",
        myAccountApiService.getEmailAddress
    );

    const { mutateCreatePatient, isLoadingCreatePatient } = useCreatePatient({
        noRedirect,
        handleClose,
        refetchPatientList,
    });

    const { mutateUpdatePatientDetails, isLoadingUpdatePatientDetails } =
        useUpdatePatientDetails();

    // const { mutateSignUp, isSuccessSignUp, SignUpResponse, isLoadingSignUp } =
    //     useSignUp();

    const { mutate: mutateSignUp, isLoadingSignUp } = useHODMutation(
        (data) => headerApiService.signUp(data),
        {
            onSuccess: (data) => {
                dispatch(userDetails(data?.data));
                const hookCity = cityLists?.find(
                    (city) => city?.cityId === data?.data?.centreDetails?.cityId
                );
                if (hookCity) dispatch(updateHookCity(hookCity));
                showCustomToast(
                    data?.data?.message || "User created successfully",
                    "success"
                );
                handleClose();
                if (setIsStep) {
                    setIsStep((prev) => prev + 1);
                }
            },
            onError: (error) => {
                showCustomToast(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Something went wrong",
                    "error"
                );
            },
        }
    );

    // console.log("setIsStep====================", isSuccessSignUp);
    const customerManagementSchema = Yup.object().shape({
        name: Yup.string()
            .required("Name is required")
            .trim("Name cannot be empty")
            .test(
                "not-empty",
                "Name cannot be empty",
                (value) => value?.trim().length > 0
            )
            .matches(VALID_CHARACTERS_REGEX, "No special characters allowed"),
        //email: Yup.string(),
        email: Yup.string()
            .required("Email is required")
            .matches(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                "Enter a valid email address"
            ),
        dob: Yup.date()
            .nullable()
            .transform((value, originalValue) => {
                return originalValue === "" ? null : value;
            })
            .required("Please select your date of birth."),
        pincode: Yup.string()
            .required("Pincode is required")
            .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits"),
        phone: Yup.string()
            .required("Mobile number is required")
            .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
        adress: Yup.string()
            .required("Address is required")
            .trim("Address cannot be empty")
            .test(
                "not-empty",
                "Address cannot be empty",
                (value) => value?.trim().length > 0
            ),
    });

    const defaultValues = useMemo(
        () => ({
            name: "",
            email: "",
            salutation: { label: "Mr", value: "Mr" },
            gender: { label: "Male", value: "M" },
            phone: isFirstUser?.mobileNumber ?? userInfo?.mobileNo,
            pincode: "",
            dob: "",
            adress: "",
        }),
        [userInfo, isFirstUser]
    );

    const methods = useForm({
        resolver: yupResolver(customerManagementSchema),
        defaultValues,
    });

    const { handleSubmit, control, reset, watch, setValue, setError } = methods;
    const values = watch();

    useEffect(() => {
        if (patientDetails && params) {
            const populatedData = AddPatientFormHandler.populatedFormData(
                patientDetails,
                userInfo,
                SALUTE,
                GENDER
            );
            reset(populatedData);
        }
    }, [params, patientDetails, userInfo]);

    // useEffect(() => {
    //     if (isSuccessSignUp) {
    //         console.log("SignUpResponse====================", SignUpResponse);
    //         const { success, message, data } = SignUpResponse || {};

    //         if (success) {
    //         }
    //     }
    // }, [isSuccessSignUp, SignUpResponse, cityLists, handleClose]);

    const MALE_SALUTES = SALUTE.filter((salute) =>
        ["mr", "dr", "master"].includes(salute.value.toLowerCase())
    ).map((salute) => salute.value.toLowerCase());

    const FEMALE_SALUTES = SALUTE.filter((salute) =>
        ["mrs", "ms", "baby"].includes(salute.value.toLowerCase())
    ).map((salute) => salute.value.toLowerCase());
    useEffect(() => {
        if (data && !params) {
            setValue("email", data?.data?.emailId);
        }
    }, [data]);

    const onTitleChangeHandler = useCallback(
        (e, newValue) => {
            const title = newValue?.value?.toLowerCase();
            setValue("salutation", newValue);

            if (MALE_SALUTES.includes(title)) {
                setValue("gender", GENDER[0]); // Male
            } else if (FEMALE_SALUTES.includes(title)) {
                setValue("gender", GENDER[1]); // Female
            } else {
                setValue("gender", null);
            }
        },
        [MALE_SALUTES, FEMALE_SALUTES]
    );

    const isFirstUserData = useMemo(
        () => Object.keys(isFirstUser).length > 0,
        [isFirstUser]
    );

    const onSubmit = async (data) => {
        if (dateUtils.isAfter(data?.dob)) {
            showCustomToast("DOB cannot be a future date.", "error");
            return;
        }

        if (data.dob === "" || data.dob === null) {
            showCustomToast("DOB is required.", "error");
            return;
        }

        try {
            setIsValidatingEmail(true);
            const response = await validateEmail(data.email);
            const { isSuccess, isValid, email, error } = response;

            if (isSuccess) {
                if (!isValid) {
                    methods.setValue("email", email);
                } else {
                    const payload = AddPatientFormHandler.getSubmittedValues(
                        data,
                        params,
                        userInfo,
                        isFirstUser
                    );

                    if (isFirstUserData) {
                        mutateSignUp(payload);
                    } else if (params) {
                        mutateUpdatePatientDetails(payload);
                    } else {
                        mutateCreatePatient(payload);
                    }
                }
            } else {
                console.log("error", error);
                setError("email", {
                    type: "manual",
                    message: error,
                });
            }
        } catch (error) {
            console.error("Error in form submission: ", error);
        } finally {
            setIsValidatingEmail(false);
        }
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <div
                className={`grid md:gap-6 gap-3 grid-cols-2 ${
                    isFirstUserData
                        ? ""
                        : "bg-white rounded-md md:px-12 px-5 md:py-10 py-5 mt-5 shadow-md"
                }`}
            >
                {/* Full Name */}
                <div className="md:col-span-1 col-span-2">
                    <p className="text-base font-medium text-gray-700 md:mb-2 mb-1">
                        Full Name
                    </p>
                    <div className="flex flex-row">
                        <div className="w-24 mr-1">
                            <RHFAutocomplete
                                onChange={onTitleChangeHandler}
                                name="salutation"
                                label="Salutation"
                                options={SALUTE || []}
                                size="small"
                                disableClearable
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                            />
                        </div>
                        <RHFTextField
                            name="name"
                            label="Enter Full Name"
                            size="small"
                        />
                    </div>
                </div>

                {/* Gender */}
                <div className="md:col-span-1 col-span-2 md:ml-6 ml:0">
                    <p className="text-base font-medium text-gray-700 md:mb-2 mb-1">
                        Gender
                    </p>
                    <RHFAutocomplete
                        disabled={
                            values?.salutation?.value.toLowerCase() !== "dr"
                        }
                        name="gender"
                        label="Gender"
                        options={GENDER || []}
                        size="small"
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                        }
                    />
                </div>

                {/* Email */}
                <div className="md:col-span-1 col-span-2">
                    <p className="text-base font-medium text-gray-700 md:mb-2 mb-1">
                        Email
                    </p>
                    <RHFTextField
                        name="email"
                        label="Enter Email"
                        size="small"
                    />
                </div>

                {/* DOB */}
                <div className="md:col-span-1 col-span-2">
                    <p className="text-base font-medium text-gray-700 md:mb-2 mb-1">
                        Date of Birth
                    </p>
                    <Controller
                        name="dob"
                        control={control}
                        rules={{ required: "Date of birth required" }}
                        render={({ field, fieldState }) => (
                            <HodDesktopDatePicker
                                value={field.value}
                                onChange={field.onChange}
                                disableFuture={true}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                </div>

                {/* Phone Number */}
                <div className="md:col-span-1 col-span-2">
                    <p className="text-base font-medium text-gray-700 md:mb-2 mb-1">
                        Phone Number
                    </p>
                    <RHFTextField
                        name="phone"
                        label="Enter Phone Number"
                        size="small"
                        disabled
                    />
                </div>

                {/* Postal Code */}
                <div className="md:col-span-1 col-span-2">
                    <p className="text-base font-medium text-gray-700 md:mb-2 mb-1">
                        Postal Code
                    </p>
                    <RHFTextField
                        name="pincode"
                        label="Enter Pincode Code"
                        size="small"
                    />
                </div>

                {/* Billing Address */}
                <div className="col-span-2">
                    <p className="text-base font-medium text-gray-700 md:mb-2 mb-1">
                        Billing Address
                    </p>
                    <RHFTextField
                        name="adress"
                        label="Billing Address"
                        size="small"
                        multiline
                        rows={4}
                    />
                </div>

                {/* Submit Button */}
                <div className="col-span-2">
                    <CustomButton
                        type="submit"
                        size="large"
                        fullWidth
                        variant="contained"
                        className="md:py-3 py-2 hover:bg-primary hover:text-white"
                        disabled={
                            isLoadingUpdatePatientDetails ||
                            isLoadingCreatePatient ||
                            isLoadingSignUp ||
                            isValidatingEmail
                        }
                        endIcon={
                            (isLoadingUpdatePatientDetails ||
                                isLoadingCreatePatient ||
                                isLoadingSignUp ||
                                isValidatingEmail) && (
                                <Iconify icon={IconCode.loadingTwoTone} />
                            )
                        }
                    >
                        Save
                    </CustomButton>
                </div>
            </div>
        </FormProvider>
    );
};

export default CreateMemberForm;
